import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, Globe, User, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';

const languages = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
];

export const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { itemCount } = useCart();
  const { user, isAuthenticated, logout } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const currentLang = languages.find(l => l.code === language);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      window.scrollTo(0, 0);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-100" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden -ml-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          {/* Logo */}
          <Link to="/" className="ml-4 mr-auto md:ml-0 md:mr-8" data-testid="logo-link">
            <span className="text-xl md:text-2xl font-light tracking-[0.3em] text-[#064E3B]">QANTRI</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/products" 
              className="text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors" 
              data-testid="nav-products"
            >
              {t('collection')}
            </Link>
            <Link 
              to="/products?category=moda" 
              className="text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors"
            >
              {t('fashion')}
            </Link>
            <Link 
              to="/products?category=casa" 
              className="text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors"
            >
              {t('casa')}
            </Link>
            <Link 
              to="/products?category=elettronica" 
              className="text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black transition-colors"
            >
              {t('electronics')}
            </Link>
            <Link 
              to="/news" 
              className="text-xs uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors"
              data-testid="nav-news"
            >
              {t('magazine')}
            </Link>
            <Link 
              to="/courses" 
              className="text-xs uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors"
              data-testid="nav-courses"
            >
              {t('academy')}
            </Link>
            <Link 
              to="/sell" 
              className="text-xs uppercase tracking-[0.2em] text-[#064E3B] hover:text-[#053d2f] transition-colors font-medium"
              data-testid="nav-sell"
            >
              Vendi
            </Link>
            <Link 
              to="/partner" 
              className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#B8962D] transition-colors font-medium"
              data-testid="nav-partner"
            >
              {t('partner')}
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Search */}
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 hover:bg-neutral-100 transition-colors"
              data-testid="search-toggle"
            >
              <Search className="h-5 w-5 text-neutral-600" />
            </button>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex p-2 hover:bg-neutral-100 transition-colors rounded-lg" data-testid="language-selector">
                  <Globe className="h-5 w-5 text-neutral-600" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[150px] z-50">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      localStorage.setItem('qantri_lang', lang.code);
                      window.location.reload();
                    }}
                    className={`cursor-pointer ${language === lang.code ? 'bg-neutral-100' : ''}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User/Login Link */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden md:flex items-center gap-2 p-2 hover:bg-neutral-100 transition-colors" data-testid="user-menu">
                    <User className="h-5 w-5 text-[#064E3B]" />
                    <span className="text-sm text-neutral-700 max-w-[100px] truncate">{user?.name || 'Account'}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="text-neutral-600">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => {
                      logout();
                      navigate('/');
                    }} 
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link" className="hidden md:block">
                <button className="p-2 hover:bg-neutral-100 transition-colors flex items-center gap-2">
                  <User className="h-5 w-5 text-neutral-600" />
                  <span className="text-sm text-neutral-600">{t('login')}</span>
                </button>
              </Link>
            )}

            {/* Cart */}
            <Link to="/cart" className="relative p-2 hover:bg-neutral-100 transition-colors" data-testid="cart-link">
              <ShoppingCart className="h-5 w-5 text-neutral-600" />
              {itemCount > 0 && (
                <span className="absolute top-0 right-0 bg-black text-white text-[10px] font-medium rounded-full h-4 w-4 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Search Bar - Expandable */}
        {searchOpen && (
          <div className="py-4 border-t border-neutral-100 animate-fadeIn">
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#064E3B]" />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-neutral-100 border border-neutral-200 rounded-lg text-sm text-neutral-800 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#064E3B]/20 focus:border-[#064E3B] transition-all"
                  autoFocus
                  data-testid="search-input"
                />
              </div>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-neutral-100 animate-fadeIn">
            <nav className="flex flex-col gap-4">
              <Link 
                to="/products" 
                className="text-sm uppercase tracking-[0.15em] text-neutral-600 hover:text-black py-2 font-medium" 
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('allProducts')}
              </Link>
              <div className="border-t border-neutral-100 pt-4">
                <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-3">{t('trending')}</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Link
                    to="/products?category=lusso"
                    className="text-sm font-medium py-2 px-3 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 rounded"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    ✨ {t('luxury')}
                  </Link>
                  <Link
                    to="/products?category=offerte"
                    className="text-sm font-medium py-2 px-3 bg-gradient-to-r from-red-100 to-orange-100 text-red-700 rounded"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    🏷️ {t('offers')}
                  </Link>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-3">{t('shopByCategory')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { name: 'Moda', key: 'moda' },
                    { name: 'Casa', key: 'casa' },
                    { name: 'Mobili', key: 'mobili' },
                    { name: 'Elettronica', key: 'elettronica' },
                    { name: 'Bellezza', key: 'bellezza' },
                    { name: 'Fitness', key: 'fitness' },
                    { name: 'Outdoor', key: 'outdoor' },
                    { name: 'Sport', key: 'sport' },
                    { name: 'Gioielli', key: 'gioielli' },
                    { name: 'Bambini', key: 'bambini' },
                    { name: 'Pet', key: 'pet' },
                    { name: 'Auto', key: 'auto' },
                    { name: 'Toys', key: 'toys' },
                    { name: 'Office', key: 'office' },
                    { name: 'DIY', key: 'diy' },
                    { name: 'Salute', key: 'salute' }
                  ].map((cat) => (
                    <Link
                      key={cat.key}
                      to={`/products?category=${cat.key}`}
                      className="text-sm text-neutral-600 hover:text-black py-2 px-3 bg-neutral-50 rounded"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="border-t border-neutral-100 pt-4 mt-2">
                <Link 
                  to="/partner" 
                  className="text-sm uppercase tracking-[0.15em] text-[#D4AF37] hover:text-[#B8962D] py-2 block font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('partner')}
                </Link>
                <Link 
                  to="/sell" 
                  className="text-sm uppercase tracking-[0.15em] text-[#064E3B] hover:text-[#065F46] py-2 flex items-center gap-2 font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  VENDI SU QANTRI
                  <span className="text-[10px] bg-[#064E3B] text-white px-1.5 py-0.5 rounded-full">NEW</span>
                </Link>
                <Link 
                  to="/support" 
                  className="text-sm uppercase tracking-[0.15em] text-neutral-600 hover:text-black py-2 block" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('support')}
                </Link>
                {isAuthenticated ? (
                  <div className="py-2">
                    <p className="text-sm text-neutral-800 font-medium mb-2">
                      Ciao, {user?.name || 'Utente'}
                    </p>
                    <button 
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                        navigate('/');
                      }}
                      className="text-sm uppercase tracking-[0.15em] text-red-600 hover:text-red-800 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      Esci
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/login" 
                    className="text-sm uppercase tracking-[0.15em] text-neutral-600 hover:text-black py-2 block" 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('login')}
                  </Link>
                )}
              </div>
            </nav>
            <div className="mt-6 pt-6 border-t border-neutral-100">
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-3">Lingua</p>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      localStorage.setItem('qantri_lang', lang.code);
                      window.location.reload();
                    }}
                    className={`px-3 py-2 text-xs ${language === lang.code ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-600'}`}
                  >
                    {lang.flag} {lang.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
